import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Eye, Edit, FileText } from "lucide-react";
import { api, toast } from "../App";
import LineItemScopeEditor from "../components/estimations/LineItemScopeEditor";

export default function DashboardEstimation({ user }) {
  const [metrics, setMetrics] = useState({ open: 0, dueNow: 0, total: 0 });
  const [estimations, setEstimations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter] = useState("this_month"); // Kept for metrics KPI default; time UI removed
  const [customDateRange] = useState({ start: "", end: "" });

  // Search filter
  const [searchTerm, setSearchTerm] = useState("");
  
  // Estimator filter — only actionable for Admin/Management/PMO/Estimation-Manager
  const [estimatorId, setEstimatorId] = useState("all");
  const [eligibleEstimators, setEligibleEstimators] = useState([]);
  const canViewAll = !!user && (
    user.access_level === "Admin"
    || user.department === "Management"
    || user.department === "PMO"
    || (user.department === "Estimation" && user.access_level === "Manager")
  );
  
  // Summary list dialog
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summaryType, setSummaryType] = useState("");
  const [summaryList, setSummaryList] = useState([]);
  
  // Version history dialog
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [currentVersions, setCurrentVersions] = useState([]);
  
  // Scope edit dialog
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [scopeData, setScopeData] = useState({
    estimation_id: "",
    standard_exclusions: "",
    delivery_process: "",
    remarks: "",
    line_items: []
  });
  
  // Quick scope edit dialog
  const [quickScopeOpen, setQuickScopeOpen] = useState(false);
  const [availableEstimations, setAvailableEstimations] = useState([]);
  const [selectedEstimationId, setSelectedEstimationId] = useState("");

  useEffect(() => {
    fetchDashboardData();
  }, [estimatorId]);

  // Load eligible estimator users once (only for view-all users)
  useEffect(() => {
    if (!canViewAll) return;
    (async () => {
      try {
        const res = await api.get("/users");
        const list = (res.data || []).filter(u =>
          u.department === "Estimation" || u.department === "Management"
        );
        setEligibleEstimators(list);
      } catch { /* silent */ }
    })();
  }, [canViewAll]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Build query params — metrics keeps a fixed 'this_month' scope for KPIs,
      // list intentionally receives no time filter (see backend: list is time-agnostic).
      let metricsParams = `time_filter=this_month`;
      let listParams = ``;
      if (canViewAll && estimatorId && estimatorId !== "all") {
        const estParam = `estimator_id=${encodeURIComponent(estimatorId)}`;
        metricsParams += `&${estParam}`;
        listParams += (listParams ? "&" : "") + estParam;
      }

      const [metricsRes, estimationsRes] = await Promise.all([
        api.get(`/dashboard/estimation/metrics?${metricsParams}`),
        api.get(`/dashboard/estimation/list${listParams ? `?${listParams}` : ""}`)
      ]);
      
      setMetrics(metricsRes.data);
      setEstimations(estimationsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async (type) => {
    try {
      // Card click still scoped to this_month for the metric drill-down.
      let params = `type=${type}&time_filter=${timeFilter}`;
      if (canViewAll && estimatorId && estimatorId !== "all") {
        params += `&estimator_id=${encodeURIComponent(estimatorId)}`;
      }
      
      const response = await api.get(`/dashboard/estimation/summary?${params}`);
      setSummaryList(response.data);
      setSummaryType(type === "open" ? "Open Estimations" : "Due Now");
      setSummaryDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load summary");
    }
  };

  const handleStatusChange = async (estimationId, newStatus) => {
    try {
      // Use PUT to update current version without creating new version
      await api.put(`/estimations/${estimationId}`, { submission_status: newStatus });
      toast.success("Status updated successfully");
      fetchDashboardData();
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleVersionHistory = async (estimationId) => {
    try {
      // Use history endpoint to get ALL versions
      const response = await api.get(`/estimations/${estimationId}/history`);
      setCurrentVersions(response.data);
      setVersionDialogOpen(true);
    } catch (error) {
      console.error("Failed to load version history:", error);
      toast.error("Failed to load version history");
    }
  };

  const handleScopeEdit = async (estimation) => {
    try {
      // Fetch full estimation data to ensure all scope fields are populated
      const response = await api.get(`/estimations/${estimation.id}`);
      const fullEstimation = response.data;
      
      // Default exclusions matching Estimations module
      const defaultExclusions = "Connection design\nPiping, Pipe Sleeves & Pipe Fitting\nElectrical & Support Items\nPEMB Structure\nLight gauge metals\nGlass Material & Ornamental fittings\nWood Member & Wood Connection\nAluminum & Stainless-steel Materials\nFence, Metal Panel & Precast Panel\nJoist, Joist Bridging, Metal Deck & Stud\nOther items not mentioned in the above inclusions.";
      
      setScopeData({
        estimation_id: fullEstimation.id,
        standard_exclusions: fullEstimation.standard_exclusions || defaultExclusions,
        delivery_process: fullEstimation.delivery_process || "",
        remarks: fullEstimation.remarks || "",
        line_items: Array.isArray(fullEstimation.line_items) ? fullEstimation.line_items : []
      });
      setScopeDialogOpen(true);
    } catch (error) {
      console.error("Failed to load estimation data:", error);
      toast.error("Failed to load estimation data");
    }
  };

  const handleScopeSave = async () => {
    try {
      // Build payload — include line_items so dashboard-managed scope changes
      // propagate while preserving each item's hours/cost/duration/award fields.
      const payload = {
        standard_exclusions: scopeData.standard_exclusions,
        delivery_process: scopeData.delivery_process,
        remarks: scopeData.remarks
      };
      if (Array.isArray(scopeData.line_items)) {
        // Strip the UI-only _created_from_dashboard flag before sending.
        payload.line_items = scopeData.line_items.map(({ _created_from_dashboard, ...rest }) => rest);
      }
      // Use PUT to update current version without creating new version
      await api.put(`/estimations/${scopeData.estimation_id}`, payload);
      toast.success("Scope updated successfully");
      setScopeDialogOpen(false);
      setQuickScopeOpen(false);
      fetchDashboardData();
    } catch (error) {
      console.error("Failed to update scope:", error);
      toast.error("Failed to update scope");
    }
  };

  const handleQuickScopeEdit = async () => {
    try {
      // Get all estimations and filter on frontend for case-insensitive match
      const response = await api.get(`/estimations`);
      const filtered = response.data.filter(est => {
        const status = est.submission_status?.toLowerCase();
        return status === 'yet to start' || status === 'in progress' || status === 'yet_to_start' || status === 'in_progress';
      });
      setAvailableEstimations(filtered);
      setQuickScopeOpen(true);
    } catch (error) {
      toast.error("Failed to load estimations");
    }
  };

  const handleQuickScopeSelect = async (estimationId) => {
    setSelectedEstimationId(estimationId);
    try {
      // Fetch full estimation detail so line_items (and complete scope fields) are populated.
      const response = await api.get(`/estimations/${estimationId}`);
      const fullEstimation = response.data;
      setScopeData({
        estimation_id: fullEstimation.id,
        standard_exclusions: fullEstimation.standard_exclusions || "",
        delivery_process: fullEstimation.delivery_process || "",
        remarks: fullEstimation.remarks || "",
        line_items: Array.isArray(fullEstimation.line_items) ? fullEstimation.line_items : []
      });
    } catch (error) {
      console.error("Failed to load estimation data:", error);
      toast.error("Failed to load estimation data");
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">Loading dashboard...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-md3-display-small">Estimation Dashboard</h1>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-end flex-wrap">
          {canViewAll && (
            <div className="w-64" data-testid="est-dash-estimator-wrap">
              <Label>Estimator</Label>
              <Select value={estimatorId} onValueChange={setEstimatorId}>
                <SelectTrigger data-testid="est-dash-estimator-trigger" className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                  <SelectValue placeholder="All estimators" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All estimators</SelectItem>
                  {eligibleEstimators.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name || ""} <span className="text-gray-500">({u.department})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="w-64">
            <Label>Estimation Number</Label>
            <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="Search by number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Custom range block removed — time filter is no longer surfaced. */}
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-amber-50/50 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border border-amber-200" onClick={() => handleCardClick("open")}>
            <CardHeader>
              <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Open Estimations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-md3-display-small text-md3-on-surface text-blue-600">{metrics.open}</div>
              <p className="text-md3-body-small text-md3-on-surface-variant mt-2">Yet to Start & In Progress</p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-purple-50/50 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border border-purple-200" onClick={() => handleCardClick("due_now")}>
            <CardHeader>
              <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Due Now</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-md3-display-small text-md3-on-surface text-red-600">{metrics.dueNow}</div>
              <p className="text-md3-body-small text-md3-on-surface-variant mt-2">Due today or overdue</p>
            </CardContent>
          </Card>

          <Card className="bg-green-50/50 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border border-green-200">
            <CardHeader>
              <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-md3-display-small text-md3-on-surface text-md3-on-surface-variant">{metrics.total}</div>
              <p className="text-md3-body-small text-md3-on-surface-variant mt-2">All estimations</p>
            </CardContent>
          </Card>
        </div>

        {/* Estimations Table */}
        <Card className="bg-blue-50/50 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border border-blue-200">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface ">Active Estimations</CardTitle>
          </CardHeader>
          <CardContent>
            <Table className="bg-md3-surface-container rounded-md3-md">
              <TableHeader>
                <TableRow>
                  <TableHead>Estimation #</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Project Name</TableHead>
                  <TableHead>Received Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Submission Status</TableHead>
                  <TableHead>Version History</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const filteredEstimations = estimations.filter(est => {
                    if (!searchTerm) return true;
                    return est.estimation_number?.toLowerCase().includes(searchTerm.toLowerCase());
                  });

                  if (filteredEstimations.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-md3-on-surface-variant">
                          {searchTerm ? `No estimations found matching "${searchTerm}"` : "No active estimations found"}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return filteredEstimations.map((est) => (
                    <TableRow
                      key={est.id}
                      data-testid={est.high_priority ? `dashboard-est-row-priority-${est.id}` : undefined}
                      className={est.high_priority ? "bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500" : ""}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {est.estimation_number}
                          {est.high_priority && (
                            <Badge className="bg-red-600 hover:bg-red-700 text-white text-[10px] px-1.5 py-0.5">
                              HIGH
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{est.version}</TableCell>
                      <TableCell>{est.project_name}</TableCell>
                      <TableCell>{est.estimation_received_date || "-"}</TableCell>
                      <TableCell className={est.high_priority ? "font-semibold text-red-700" : ""}>{est.due_date || "-"}</TableCell>
                      <TableCell>
                        <Select
                          value={est.submission_status || "yet_to_start"}
                          onValueChange={(value) => handleStatusChange(est.id, value)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yet_to_start">Yet to Start</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="reviewed" data-testid={`est-status-reviewed-${est.id}`}>Reviewed</SelectItem>
                            {canViewAll && (
                              <SelectItem value="submitted" data-testid={`est-status-submitted-${est.id}`}>Submitted</SelectItem>
                            )}
                            <SelectItem value="hold">Hold</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                           onClick={() => handleVersionHistory(est.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                           onClick={() => handleScopeEdit(est)}>
                          Scope Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Summary Dialog */}
        <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{summaryType}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {summaryList.map((item) => (
                <div key={item.id} className="p-3 border rounded hover:bg-md3-surface">
                  <div className="font-medium">{item.estimation_number}</div>
                  <div className="text-md3-body-small text-md3-on-surface-variant">{item.project_name}</div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Version History Dialog */}
        <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Version History</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {currentVersions.map((version, index) => {
                const bgColors = ['bg-blue-50/50 border-blue-200', 'bg-green-50/50 border-green-200', 'bg-purple-50/50 border-purple-200', 'bg-amber-50/50 border-amber-200', 'bg-pink-50/50 border-pink-200', 'bg-cyan-50/50 border-cyan-200'];
                return (
                <Card key={version.id} className={`border ${bgColors[index % bgColors.length]}`}>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                      <div><strong>Version:</strong> {version.version}</div>
                      <div><strong>Estimation Number:</strong> {version.estimation_number}</div>
                      <div><strong>Project Name:</strong> {version.project_name}</div>
                      <div><strong>Received Date:</strong> {version.estimation_received_date || "-"}</div>
                      <div><strong>Due Date:</strong> {version.due_date || "-"}</div>
                      <div><strong>Submission Status:</strong> 
                        <span className="ml-2 px-2 py-1 rounded text-xs bg-md3-surface-container">
                          {version.submission_status?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="col-span-2"><strong>Remarks:</strong> {version.remarks || "No remarks"}</div>
                      <div><strong>Created:</strong> {version.created_at ? new Date(version.created_at).toLocaleDateString() : "-"}</div>
                    </div>
                  </CardContent>
                </Card>
              );
              })}
            </div>
          </DialogContent>
        </Dialog>

        {/* Scope Edit Dialog */}
        <Dialog open={scopeDialogOpen} onOpenChange={setScopeDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Edit Scope</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="border border-gray-200 rounded-md p-4 bg-white">
                <LineItemScopeEditor
                  lineItems={scopeData.line_items}
                  onChange={(items) => setScopeData({ ...scopeData, line_items: items })}
                />
              </div>
              <div>
                <Label>Standard Exclusions <span className="text-xs font-normal text-gray-500">(apply to entire proposal)</span></Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={scopeData.standard_exclusions}
                  onChange={(e) => setScopeData({ ...scopeData, standard_exclusions: e.target.value })}
                  rows={6}
                  placeholder="Enter standard exclusions (one per line)"
                />
              </div>
              <div>
                <Label>Delivery Process</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={scopeData.delivery_process}
                  onChange={(e) => setScopeData({ ...scopeData, delivery_process: e.target.value })}
                  rows={4}
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={scopeData.remarks}
                  onChange={(e) => setScopeData({ ...scopeData, remarks: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2"  onClick={() => setScopeDialogOpen(false)}>Cancel</Button>
                <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleScopeSave}>Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Scope Edit Dialog */}
        <Dialog open={quickScopeOpen} onOpenChange={setQuickScopeOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Quick Scope Edit</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div>
                <Label>Select Estimation</Label>
                <Select value={selectedEstimationId} onValueChange={handleQuickScopeSelect}>
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select estimation..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableEstimations.map((est) => (
                      <SelectItem key={est.id} value={est.id}>
                        {est.estimation_number} - {est.project_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedEstimationId && (
                <>
                  <div className="border border-gray-200 rounded-md p-4 bg-white">
                    <LineItemScopeEditor
                      lineItems={scopeData.line_items}
                      onChange={(items) => setScopeData({ ...scopeData, line_items: items })}
                    />
                  </div>
                  <div>
                    <Label>Standard Exclusions <span className="text-xs font-normal text-gray-500">(apply to entire proposal)</span></Label>
                    <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={scopeData.standard_exclusions}
                      onChange={(e) => setScopeData({ ...scopeData, standard_exclusions: e.target.value })}
                      rows={6}
                      placeholder="Enter standard exclusions (one per line)"
                    />
                  </div>
                  <div>
                    <Label>Delivery Process</Label>
                    <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={scopeData.delivery_process}
                      onChange={(e) => setScopeData({ ...scopeData, delivery_process: e.target.value })}
                      rows={4}
                    />
                  </div>
                  <div>
                    <Label>Remarks</Label>
                    <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={scopeData.remarks}
                      onChange={(e) => setScopeData({ ...scopeData, remarks: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2"  onClick={() => setQuickScopeOpen(false)}>Cancel</Button>
                    <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleScopeSave}>Save</Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
